import React from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar, MapPin, Download } from "lucide-react";
import { SafeHTML } from "@/components/site/RichTextEditor";
import ShareReactBar from "@/components/site/ShareReactBar";

const STATUS_COLOR = {
  a_venir: "bg-cef-blue text-white",
  en_cours: "bg-cef-green",
  termine: "bg-white",
};
const STATUS_LABEL = { a_venir: "À venir", en_cours: "En cours", termine: "Terminé" };
const TYPE_COLOR = {
  evenement: "bg-cef-blue text-white",
  mobilisation: "bg-cef-pink text-white",
  outil: "bg-cef-yellow",
};

export default function ActionDetail() {
  const { id } = useParams();
  const [action, setAction] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    api.get(`/actions/${id}`).then((r) => setAction(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h1 className="font-display font-black text-3xl" data-testid="action-not-found">Action introuvable</h1>
        <Link to="/actions" className="neo-btn bg-cef-yellow mt-4 inline-flex">← Retour aux actions</Link>
      </div>
    );
  }
  if (!action) return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">Chargement…</div>;

  const isHTML = (action.content || "").trim().startsWith("<");
  const dateLabel = (() => {
    if (!action.date) return null;
    try { return new Date(action.date).toLocaleString("fr-BE", { dateStyle: "long", timeStyle: "short" }); }
    catch { return action.date; }
  })();

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="action-detail">
      <Link to="/actions" className="inline-flex items-center gap-2 text-sm font-bold hover:underline mb-6" data-testid="back-to-actions">
        <ArrowLeft className="w-4 h-4" /> Toutes les actions
      </Link>

      <div className="flex flex-wrap items-center gap-2">
        <span className={`chip ${TYPE_COLOR[action.type] || "bg-white"}`} data-testid="action-type">{action.type}</span>
        <span className={`chip ${STATUS_COLOR[action.status] || "bg-white"}`} data-testid="action-status">{STATUS_LABEL[action.status] || action.status}</span>
      </div>

      <h1 className="mt-3 font-display font-black text-3xl sm:text-5xl tracking-tighter leading-tight" data-testid="action-title">{action.title}</h1>

      <div className="mt-4 flex flex-wrap gap-4 text-sm text-neutral-700">
        {dateLabel && <div className="inline-flex items-center gap-1.5" data-testid="action-date"><Calendar className="w-4 h-4" /> {dateLabel}</div>}
        {action.location && <div className="inline-flex items-center gap-1.5" data-testid="action-location"><MapPin className="w-4 h-4" /> {action.location}</div>}
      </div>

      {action.image_url && (
        <img src={action.image_url} alt={action.title} className="mt-6 w-full border-2 border-black rounded-md shadow-brutal" data-testid="action-image" />
      )}

      {action.description && (
        <p className="mt-6 text-lg leading-relaxed text-neutral-800" data-testid="action-description">{action.description}</p>
      )}

      {action.content && (
        <div className="mt-6" data-testid="action-content">
          {isHTML
            ? <SafeHTML html={action.content} className="prose-lg" />
            : <div className="prose max-w-none text-base leading-relaxed whitespace-pre-wrap">{action.content}</div>}
        </div>
      )}

      {action.kit_url && (
        <a href={action.kit_url} target="_blank" rel="noreferrer" className="neo-btn bg-cef-yellow mt-8 inline-flex" data-testid="action-kit-link">
          <Download className="w-4 h-4" /> Télécharger le pack action
        </a>
      )}

      <ShareReactBar title={action.title} kind="Action" />
    </article>
  );
}
